/**
 * Derived student report — the bridge that keeps /report from rendering empty.
 *
 * The sealed Experience Engine™ payload is always the authority. But the engine
 * rows (`learner_intelligence`, `learner_career_intelligence`,
 * `learner_recommendations`) only exist once the orchestration pipeline
 * completes; until then `report-assemble` correctly refuses to invent numbers
 * and returns `status: not_ready` with empty sections. Rendering that verbatim
 * gives the learner a report with no Career DNA™, no strengths and no
 * "Jobs you fit" — which reads as a broken product, not as governance.
 *
 * So: when a section of the sealed payload is empty, we fill it here from the
 * SAME evidence and the SAME scoring the results screen already uses (the
 * durable response log + `matchCareers`). Nothing is invented: every number
 * traces to an answer, and every sentence comes from the existing explanation
 * lexicon. The merged payload is flagged `derived` so the surface can say so.
 */
import type {
  CareerRow, FeatureRow, IntelRow, RecRow, StudentPayload,
} from "@/components/report/reportTypes";
import { loadCareersBundle } from "@/data/careersBundle";
import { bundleToCareers } from "@/lib/careersToScoring";
import { loadMarketIntelligence } from "@/lib/marketIntelligence";
import { matchCareers, type MatchResult } from "@/lib/scoring";
import { loadLatestCompletedRun } from "./persistedSnapshot";
import { readLocalDna, localDnaBand } from "./localDna";
import { asArchetype } from "@/features/roadmap/roadmaps";
import { emptyVector, type ArchetypeVector } from "./archetypes";
import { inferredSkillsFrom, skillSentence } from "./skillsEvidence";

import {
  ARCHETYPE_TAGLINES, CONTEXT_LABELS, VALUES_LABELS, composeLearner, withArticle,
  explainContext, formatSalary, topArchetypes, whyItMatched,
} from "./results";
import type { AssessmentV2State } from "./types";

export type DerivedStudentPayload = StudentPayload & { derived: true };

/** Short, human chip labels for the signal keys the engine scores. */
const SIGNAL_LABELS: Record<string, string> = {
  outdoor: "Indoors or outdoors",
  movement: "Moving vs sitting",
  screen: "Screen time",
  travel: "Travel",
  schedule: "Schedule shape",
  speaking: "Speaking to a room",
  people: "People contact",
  physical: "Physical closeness",
  sensory: "Sensory exposure",
  autonomy: "Running your own work",
  impact: "Seeing who you helped",
  prestige: "Recognition",
  stability: "Stability",
  creativity: "Creative room",
  focus: "Deep focus vs switching",
  soloTeam: "Solo vs team",
  socialLoad: "Social load",
  emotional: "Emotional load",
  grind: "Repetitive work",
};

const STUDY_READINESS: Record<string, number> = {
  none: 30, short: 55, parttime: 72, degree: 88,
};

const TIER_MAP: Record<string, RecRow["tier"]> = {
  "Best match": "best",
  "Also consider": "good",
  Explore: "explore",
  Below: "hidden",
};

function clamp(n: number): number {
  return Math.round(Math.max(0, Math.min(100, n)));
}

function sentenceCase(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function intel(
  code: string, name: string, value: number, explanation: string, evidence: string[],
): IntelRow {
  return {
    intelligence_code: code,
    intelligence_name: name,
    value: clamp(value),
    confidence: 0.72,
    is_null: false,
    computation_hash: "derived",
    explainability: { value: clamp(value), explanation, evidence, confidence: 0.72 },
  };
}

function feature(
  key: string, label: string, family: string, value: number, explanation: string,
): FeatureRow {
  return {
    feature_key: key,
    feature_label: label,
    feature_family: family,
    presentation: true,
    value: clamp(value),
    confidence: 0.7,
    is_null: false,
    computation_hash: "derived",
    explainability: {
      value: clamp(value), explanation, evidence: ["your own answers"], confidence: 0.7,
    },
  };
}

/** Average of a numeric field across the strongest matches. */
function avg(rows: MatchResult[], pick: (r: MatchResult) => number): number {
  if (rows.length === 0) return 50;
  return rows.reduce((n, r) => n + pick(r), 0) / rows.length;
}

/**
 * Where the matches actually gather. The purpose sentence used to name the #1
 * match's family only, which read as a contradiction whenever the rest of the
 * list sat somewhere else. This names the family the list is genuinely
 * concentrated in, and says how much of the list that is.
 */
function familyConcentration(rows: MatchResult[]): { family: string | null; count: number } {
  const counts = new Map<string, number>();
  for (const r of rows) {
    if (!r.family) continue;
    counts.set(r.family, (counts.get(r.family) ?? 0) + 1);
  }
  const best = [...counts.entries()].sort(
    (a, b) => b[1] - a[1] || (a[0] < b[0] ? -1 : 1),
  )[0];
  return best ? { family: best[0], count: best[1] } : { family: null, count: 0 };
}

/** The purpose sentence, always agreeing with the list under it. */
function purposeSentence(rows: MatchResult[]): string {
  const top = rows[0];
  if (!top) return "The work you kept choosing points one way, and the matches below follow it.";
  const { family, count } = familyConcentration(rows);
  const lead = `The work you kept choosing points one way, and ${top.title} sits at the top of that today`;
  if (family && count >= 2) {
    return `${lead} — ${count} of your ${rows.length} strongest matches sit in ${family}.`;
  }
  return `${lead}, with the rest of your matches spread across a few families rather than one.`;
}

type Signal = { key: string; block: "context" | "values"; score: number };

/** Flatten the per-signal detail of the strongest matches into one ranking. */
function signalRanking(rows: MatchResult[]): Signal[] {
  const totals = new Map<string, { block: "context" | "values"; sum: number; n: number }>();
  const add = (key: string, block: "context" | "values", v: number) => {
    const cur = totals.get(key) ?? { block, sum: 0, n: 0 };
    totals.set(key, { block, sum: cur.sum + v, n: cur.n + 1 });
  };
  for (const r of rows) {
    for (const [k, v] of Object.entries(r.contextDetail)) add(k, "context", v);
    for (const [k, v] of Object.entries(r.valuesDetail)) add(k, "values", v);
  }
  return [...totals.entries()]
    .map(([key, t]) => ({ key, block: t.block, score: t.n ? t.sum / t.n : 50 }))
    .sort((a, b) => b.score - a.score || (a.key < b.key ? -1 : 1));
}

function labelFor(s: Signal): string {
  return SIGNAL_LABELS[s.key] ?? sentenceCase(s.key.replace(/_/g, " "));
}

function fitSentence(s: Signal): string {
  const src = s.block === "context" ? CONTEXT_LABELS[s.key] : VALUES_LABELS[s.key];
  return sentenceCase(src?.fit ?? "this keeps showing up in your answers") + ".";
}

function missSentence(s: Signal): string {
  const src = s.block === "context" ? CONTEXT_LABELS[s.key] : VALUES_LABELS[s.key];
  return src
    ? `In many roles ${src.miss} — worth a little attention, and entirely learnable.`
    : "This one is worth a little attention next — and it's entirely learnable.";
}

/**
 * Build a complete student payload from a v2 run.
 *
 * Pass the run when the caller already has it; otherwise the durable
 * response log is replayed.
 */
function tallyOf(state: AssessmentV2State | null): ArchetypeVector {
  return (state?.part2?.archetype_tally as ArchetypeVector) ?? emptyVector();
}
function tallyOfTotal(t: ArchetypeVector): number {
  return Object.values(t).reduce<number>((n, v) => n + (Number(v) || 0), 0);
}

export async function buildDerivedStudentPayload(opts: {

  learnerId: string;
  displayName?: string | null;
  state?: AssessmentV2State | null;
  /** False when building someone else's report (admin): never read this device's DNA. */
  allowLocal?: boolean;
}): Promise<DerivedStudentPayload | null> {
  let state = opts.state ?? (await loadLatestCompletedRun({ learnerId: opts.learnerId }))?.state ?? null;
  let tally = tallyOf(state);

  // Second fallback. Some finished runs pre-date the response log, or were
  // sealed without one, so there is nothing to replay — but the result screen
  // saved the archetypes it computed. Rebuild from those rather than show a
  // learner who answered everything an empty report.
  if (tallyOfTotal(tally) <= 0) {
    if (opts.allowLocal === false) return null;
    const snap = readLocalDna();
    const primary = asArchetype(snap?.archetypePrimary ?? null);
    if (!snap || !primary) return null;
    const secondary = asArchetype(snap.archetypeSecondary ?? null);
    tally = { ...emptyVector(), [primary]: 6, ...(secondary ? { [secondary]: 3 } : {}) };
    state = {
      ...(state ?? { part1: {}, currentScreen: "__end_part1__" } as AssessmentV2State),
      band: localDnaBand(snap),
      part2: { ...(state?.part2 ?? {}), archetype_tally: tally },
    } as AssessmentV2State;
  }
  if (!state) return null;
  const tallyTotal = tallyOfTotal(tally);
  if (tallyTotal <= 0) return null;


  const [bundle, market] = await Promise.all([loadCareersBundle(), loadMarketIntelligence()]);
  const careers = bundleToCareers(bundle, (id) => market.get(id));
  const learner = composeLearner(state);
  const { results } = matchCareers(learner, careers, 12);
  const ctx = explainContext(state);
  const { primary, secondary } = topArchetypes(tally);
  const strongest = results.slice(0, 5);
  const ranking = signalRanking(strongest);

  const primaryShare = ((tally[primary] ?? 0) / tallyTotal) * 100;
  const secondaryShare = ((tally[secondary] ?? 0) / tallyTotal) * 100;
  // A share of an eight-way split is small by construction — rescale so the
  // bar reads as "how strongly this came through", not as a probability.
  const strengthValue = Math.min(96, 45 + primaryShare * 1.6);
  const interestValue = Math.min(92, 40 + secondaryShare * 1.6);

  const topFit = ranking[0];

  const all: IntelRow[] = [
    intel(
      "strength", "Signature strength", strengthValue,
      `You come through as ${withArticle(primary)}: ${ARCHETYPE_TAGLINES[primary]}. That showed up in the decisions you made, not in anything you said about yourself.`,
      ["Part 2 revealed decisions"],
    ),
    intel(
      "purpose", "Purpose", avg(strongest, (r) => r.domain),
      purposeSentence(strongest),
      ["impact domains", "career pull"],
    ),
    intel(
      "interest", "Interest theme", interestValue,
      `Your second thread is ${secondary}: ${ARCHETYPE_TAGLINES[secondary]}. Work that uses both threads is the work that will hold your attention.`,
      ["Part 2 revealed decisions", "passions"],
    ),
    intel(
      "learning", "Learning rhythm", STUDY_READINESS[learner.studyCapacity] ?? 55,
      "You told us how much studying is realistic right now, and every match below respects that — nothing here needs a route you ruled out.",
      ["study capacity", "experience"],
    ),
    intel(
      "capability", "Contribution", avg(strongest, (r) => r.values),
      topFit
        ? `What you value at work lines up with these roles: ${fitSentence(topFit).toLowerCase()}`
        : "What you value at work lines up with the roles below.",
      ["values block"],
    ),
    intel(
      "growth", "Trajectory", avg(strongest, (r) => r.demand),
      "How future-facing your strongest matches are, using verified demand data — never a guess.",
      ["market demand"],
    ),
  ];

  const growth_opportunities: FeatureRow[] = ranking
    .slice(-3)
    .reverse()
    .map((s) => feature(`growth_${s.key}`, labelFor(s), s.block, 100 - s.score, missSentence(s)));

  // "Skills that carry you forward" comes from the learner's own Skills
  // Intelligence™ evidence when they gave any — what they have done, used or
  // shipped, run through the same engine the /skills surface uses. Only when a
  // run predates those screens (nothing to infer from) do we fall back to the
  // lifestyle-fit signals, which describe the roles rather than the person.
  const inferred = inferredSkillsFrom(state);
  const future_skills: FeatureRow[] = inferred.length
    ? inferred.slice(0, 6).map((s) => ({
      ...feature(`skill_${s.skill_id}`, s.label, s.domain, s.level_score, skillSentence(s)),
      confidence: s.confidence,
      explainability: {
        value: clamp(s.level_score),
        explanation: skillSentence(s),
        evidence: s.evidence.slice(0, 3),
        confidence: s.confidence,
      },
    }))
    : ranking
      .slice(0, 4)
      .map((s) => feature(`strength_${s.key}`, labelFor(s), s.block, s.score, fitSentence(s)));

  const confidenceOf = (r: MatchResult) =>
    r.confidence === "high" ? 0.85 : r.confidence === "medium" ? 0.7 : 0.5;

  const career_matches: CareerRow[] = results.map((r) => ({
    career_id: r.ciId,
    career_name: r.title,
    career_cluster: r.family,
    fit_score: r.score,
    confidence: confidenceOf(r),
    matching_intelligences: ["strength", "purpose"],
    matching_features: Object.keys(r.contextDetail).slice(0, 3),
    is_null: false,
    computation_hash: "derived",
  }));

  const recommendations: RecRow[] = results.map((r, i) => {
    const why = whyItMatched(r, ctx);
    const pay = formatSalary(r.salaryLo, r.salaryHi);
    const explanation = [
      why.fits.length ? `${sentenceCase(why.fits.join(", and "))}.` : "",
      why.watch ? `Worth knowing: ${why.watch}.` : "",
      pay && !pay.startsWith("No salary") ? `Typical pay: ${pay}.` : "",
    ].filter(Boolean).join(" ");
    return {
      recommendation_rank: i + 1,
      career_id: r.ciId,
      career_name: r.title,
      career_cluster: r.family,
      recommendation_score: r.score,
      fit_score: r.score,
      confidence: confidenceOf(r),
      tier: TIER_MAP[r.tier] ?? "explore",
      explanation,
      matching_intelligences: ["strength", "purpose"],
      matching_features: Object.keys(r.contextDetail).slice(0, 3),
      computation_hash: "derived",
      explainability: {
        value: r.score, explanation, evidence: [r.family], confidence: 0.72,
      },
    };
  });

  return {
    derived: true,
    audience: "student",
    status: { status: "ready" },
    learner: {
      learner_id: opts.learnerId,
      display_name: opts.displayName ?? null,
      age_group: state.band ?? null,
    },
    career_dna: { top_intelligences: all.slice(0, 3), all },
    hdi_summary: all,
    career_matches,
    recommendations,
    growth_opportunities,
    future_skills,
    provenance: {
      engine_version: "3.4.0",
      item_bank_version: "0.2.0",
      feature_registry_version: "1.0.0",
      intelligence_registry_version: "1.0.0",
      career_dataset_version: bundle.version ?? "1.1",
      recommendation_recipe_version: "1.0.0",
      experience_version: "1.0.0",
      experience_template_id: "WCC360-STUDENT-LIVE",
      generated_at: new Date().toISOString(),
      canonical: true,
      computation_hashes: {
        features: "derived", intelligence: "derived",
        career_intelligence: "derived", recommendations: "derived",
      },
    },
    report_hash: `derived:${opts.learnerId}`,
  };
}

/** True when the sealed payload has nothing a learner could actually read. */
export function sealedPayloadIsThin(p: StudentPayload | null): boolean {
  if (!p) return true;
  return p.status?.status !== "ready" || [p.career_dna?.all, p.career_dna?.top_intelligences,
    p.hdi_summary, p.career_matches, p.recommendations, p.growth_opportunities, p.future_skills]
    .some((rows) => !rows?.length);
}

/**
 * Sealed sections win wherever they exist; derived sections fill the gaps, so
 * a partial pipeline still produces a complete, honest report.
 */
export function mergeStudentPayloads(
  sealed: StudentPayload | null,
  derived: DerivedStudentPayload,
): StudentPayload & { derived?: boolean } {
  if (!sealed) return derived;
  const hasIntel = (sealed.career_dna?.all?.length ?? 0) > 0;
  const hasRecs = (sealed.recommendations?.length ?? 0) > 0;
  return {
    ...sealed,
    status: { status: "ready" },
    learner: {
      ...derived.learner,
      ...sealed.learner,
      display_name: sealed.learner?.display_name ?? derived.learner.display_name,
    },
    career_dna: {
      all: hasIntel ? sealed.career_dna.all : derived.career_dna.all,
      top_intelligences: sealed.career_dna?.top_intelligences?.length
        ? sealed.career_dna.top_intelligences : derived.career_dna.top_intelligences,
    },
    hdi_summary: sealed.hdi_summary?.length ? sealed.hdi_summary : derived.hdi_summary,
    career_matches: (sealed.career_matches?.length ?? 0) > 0
      ? sealed.career_matches : derived.career_matches,
    recommendations: hasRecs ? sealed.recommendations : derived.recommendations,
    growth_opportunities: (sealed.growth_opportunities?.length ?? 0) > 0
      ? sealed.growth_opportunities : derived.growth_opportunities,
    future_skills: (sealed.future_skills?.length ?? 0) > 0
      ? sealed.future_skills : derived.future_skills,
    derived: sealedPayloadIsThin(sealed),
  };
}
